import type {
  AIConversation, Attempt, BossRecord, CalibrationLogEntry, CalibrationSummary, Course, Exam, ExamResult, FeynmanRecord,
  Flashcard, Goal, MemoryPalace, Mistake, Note, Notification, PersonalBests, PlannerEvent, Profile, Quest, ReviewLog,
  Settings, SkillEstimate, StudySession, SubjectId, WritingRecord, XPEvent, AssessmentRecord,
} from './types';
import type { CalibrationRun } from '@/engine/calibration';
import { detectLang } from '@/i18n';

export const SCHEMA_VERSION = 4;

export interface ActivityDay {
  attempts: number;
  correct: number;
  xp: number;
  ms: number;
}

export interface AppState {
  schema: number;
  profile: Profile | null;
  /** Evidence-based estimates. A missing key means UNKNOWN. */
  estimates: Record<string, SkillEstimate>;
  attempts: Attempt[];
  sessions: StudySession[];
  mistakes: Mistake[];
  flashcards: Flashcard[];
  reviewLog: ReviewLog[];
  xp: { total: number; events: XPEvent[] };
  quests: Quest[];
  achievements: Record<string, number>;
  bosses: Record<string, BossRecord>;
  courses: Course[];
  exams: Exam[];
  examResults: ExamResult[];
  planner: PlannerEvent[];
  goals: Goal[];
  notes: Note[];
  palaces: MemoryPalace[];
  notifications: Notification[];
  /** Recent question fingerprints per skill (exposure control). */
  exposure: Record<string, string[]>;
  calibration: {
    active: CalibrationRun | null;
    summaries: Partial<Record<SubjectId, CalibrationSummary>>;
    log: CalibrationLogEntry[];
  };
  activity: Record<string, ActivityDay>;
  personalBests: PersonalBests;
  feynman: FeynmanRecord[];
  writing: WritingRecord[];
  /** Reports from the Assessment tab (official competence aims). */
  assessments: AssessmentRecord[];
  conversations: AIConversation[];
  streak: { current: number; best: number; lastDay: string | null };
  settings: Settings;
  /** Running counters used by quests/achievements. */
  counters: Record<string, number>;
}

export const DEFAULT_SETTINGS: Settings = {
  language: detectLang(),
  band: 'auto',
  textLevel: 'auto',
  appearance: 'night',
  motion: 'full',
  motionFollowSystem: true,
  effects: defaultEffects(),
  effectsAuto: true,
  sound: true,
  volume: 0.6,
  speechRate: 1,
  readAloud: false,
  showProvenance: true,
  calibrationLength: 'standard',
  sessionLength: 'normal',
  breakReminders: false,
  aiProvider: 'none',
  aiModel: 'claude-opus-5-5',
};

/** Modest starting point on low-end hardware; the frame monitor can lower it further. */
function defaultEffects(): Settings['effects'] {
  if (typeof navigator === 'undefined') return 'medium';
  const cores = navigator.hardwareConcurrency ?? 8;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  return cores <= 4 || mem <= 4 ? 'low' : 'medium';
}

/** Accepts settings saved by any earlier version and returns a complete, valid object. */
export function normalizeSettings(raw: unknown): Settings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const s: Settings = { ...DEFAULT_SETTINGS, ...(r as Partial<Settings>) };
  // v3 used motion 'system' | 'full' | 'reduced'
  if (r.motion === 'system') { s.motion = 'full'; s.motionFollowSystem = true; }
  if (!['off', 'reduced', 'full'].includes(s.motion)) s.motion = 'full';
  // v3 used effects 'full' | 'balanced' | 'minimal'
  const legacy: Record<string, Settings['effects']> = { full: 'high', balanced: 'medium', minimal: 'low' };
  if (typeof r.effects === 'string' && legacy[r.effects]) s.effects = legacy[r.effects];
  if (!['off', 'low', 'medium', 'high'].includes(s.effects)) s.effects = 'medium';
  if (!['nb', 'en'].includes(s.language)) s.language = detectLang();
  if (!['auto', 'junior', 'middle', 'senior'].includes(s.band)) s.band = 'auto';
  // Older saves chose 'Grade 1–4 wording' through the band setting; carry that over as easy text.
  if (!['auto', 'easy', 'normal', 'detailed'].includes(String(r.textLevel))) {
    s.textLevel = s.band === 'junior' ? 'easy' : 'auto';
    s.band = 'auto'; // the age band now always follows the learner's grade
  }
  if (s.appearance !== 'day') s.appearance = 'night';
  s.volume = Math.min(1, Math.max(0, Number(s.volume) || 0));
  s.speechRate = Math.min(1.6, Math.max(0.5, Number(s.speechRate) || 1));
  return s;
}

export function initialState(): AppState {
  return {
    schema: SCHEMA_VERSION,
    profile: null,
    estimates: {},
    attempts: [],
    sessions: [],
    mistakes: [],
    flashcards: [],
    reviewLog: [],
    xp: { total: 0, events: [] },
    quests: [],
    achievements: {},
    bosses: {},
    courses: [],
    exams: [],
    examResults: [],
    planner: [],
    goals: [],
    notes: [],
    palaces: [],
    notifications: [],
    exposure: {},
    calibration: { active: null, summaries: {}, log: [] },
    activity: {},
    personalBests: { longestCorrectStreak: 0, hardestCorrect: 0, mostQuestionsInDay: 0, bestExamScore: 0 },
    feynman: [],
    writing: [],
    assessments: [],
    conversations: [],
    streak: { current: 0, best: 0, lastDay: null },
    settings: DEFAULT_SETTINGS,
    counters: {},
  };
}
